/**
 * Language selector.
 *
 * Persists through the `portal_set_locale` RPC, on the PostgREST path — portal-
 * local data, per CLAUDE.md §3.
 *
 * ⚠️ NOT a direct table update, and not an RLS update policy. `authenticated`
 *    already holds a table-level UPDATE grant on everything in `public` from
 *    Supabase's bootstrap, so adding an update policy to portal_users authorises
 *    EVERY column — including `documento`. That was tried, exploited, and
 *    reverted; see the migration header. The RPC picks the row with auth.uid()
 *    and writes one column, so neither is reachable.
 *
 * Failing to persist is not fatal. The UI still switches; the choice simply does
 * not follow the customer to another device. Blocking a language change on a
 * database write would be a worse trade.
 */
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches, useNavigate, useParams } from 'react-router-dom';

import { getSupabaseClient } from '../api/supabase.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { SUPPORTED_LOCALES, currentLocale, type Locale } from '../i18n/index.ts';
import { isRouteId, translatePath } from '../i18n/routes.ts';
import styles from './LocaleSelector.module.css';

/* The closed control shows the flag, not the locale name — see the CSS
   module. Which flag is a function of the selected value, so it can't be a
   static background-image in CSS; the chevron layer next to it can, and is
   carried there as --chevron. */
const FLAGS: Record<Locale, string> = {
  'pt-BR': '/brand/flag-br.svg',
  'es-CL': '/brand/flag-cl.svg',
  'es-EC': '/brand/flag-ec.svg',
};

export function LocaleSelector(): React.JSX.Element {
  const id = useId();
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const matches = useMatches();
  const params = useParams();
  const [saving, setSaving] = useState(false);
  const selected = currentLocale();

  // Which page we are on, in the language-independent form. Read off the route
  // `handle` rather than parsed out of the pathname, so a slug rename cannot
  // silently break the switch.
  const handle = matches[matches.length - 1]?.handle;
  const routeId =
    typeof handle === 'object' && handle !== null && 'routeId' in handle && isRouteId(handle.routeId)
      ? handle.routeId
      : undefined;

  const onChange = useCallback(
    async (next: Locale) => {
      setSaving(true);

      // BEFORE navigating, and in the same event so React batches them.
      //
      // AuthContext re-evaluates the stored-locale redirect on every navigation
      // and treats portal_users.locale as the truth about what the customer
      // wants. Navigating first would have it read the stale previous value,
      // conclude the new URL is wrong, and bounce straight back — the switch
      // did nothing until a reload refetched the row.
      auth.setStoredLocale(next);

      // Navigate rather than calling setLocale: the locale branch's loader owns
      // the switch, so the URL and the rendered language change together. Doing
      // both would paint the new language at the old address for a frame.
      navigate(translatePath(routeId, next, { osId: params['osId'] }), { replace: true });

      if (auth.user?.id) {
        const { error } = await getSupabaseClient().rpc('portal_set_locale', { p_locale: next });
        if (error) {
          console.error('Could not persist locale preference:', error.message);
        }
      }
      setSaving(false);
    },
    [auth, navigate, params, routeId],
  );

  return (
    <div className={styles['group']}>
      <label htmlFor={id} className={styles['label']}>
        {t('locale.label')}
      </label>
      <select
        id={id}
        className={styles['select']}
        style={{ backgroundImage: `url(${FLAGS[selected]}), var(--chevron)` }}
        value={selected}
        disabled={saving}
        onChange={(e) => void onChange(e.target.value as Locale)}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l} value={l}>
            {t(`locale.${l}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
